import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'

export const LOG_ENDPOINT = '/api/logs'
export const LOG_QUEUE_LIMIT = 200
export const LOG_FLUSH_RECORD_THRESHOLD = 20
export const LOG_FLUSH_INTERVAL_MS = 1000
export const LOG_REQUEST_MAX_RECORDS = 100
// Fetch keepalive requests have a much smaller browser budget than the
// middleware's 256 KB cap. Staying below 64 KB keeps unload flushes portable.
export const LOG_REQUEST_MAX_BYTES = 60 * 1024

const pendingRecords = []

let flushTimer = null
let initialized = false
let webVitalsRegistered = false
let authenticatedContext = {
  userHash: null,
  organizationId: null,
}

const isObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const normalizeContextValue = (value) => {
  const normalizedValue = String(value ?? '').trim()
  return normalizedValue || null
}

const getUtf8ByteLength = (value) => {
  if (typeof TextEncoder === 'function') {
    return new TextEncoder().encode(value).length
  }

  return value.length
}

const getAppContext = () => {
  const params = new URLSearchParams(window.location.search)
  if (params.get('source') === 'webview') return 'webview'

  try {
    if (window.self !== window.top) return 'iframe'
  } catch {
    return 'iframe'
  }

  return 'standalone'
}

const withLogContext = (record) => {
  const contextProperties = {
    ...(typeof window !== 'undefined' ? { appContext: getAppContext() } : {}),
    ...(authenticatedContext.userHash
      ? { user_id_hash: authenticatedContext.userHash }
      : {}),
    ...(authenticatedContext.organizationId
      ? { organization_id: authenticatedContext.organizationId }
      : {}),
  }
  const properties = isObject(record.properties) ? record.properties : {}

  if (
    Object.keys(contextProperties).length === 0 &&
    Object.keys(properties).length === 0
  ) {
    return record
  }

  return {
    ...record,
    properties: {
      ...contextProperties,
      ...properties,
    },
  }
}

const stringifyException = (exception) => {
  if (typeof exception === 'string') return exception
  if (typeof exception?.stack === 'string') return exception.stack
  if (typeof exception?.message === 'string') {
    return `${exception.name || 'Error'}: ${exception.message}`
  }
  return String(exception || 'Frontend exception')
}

const normalizeRecord = (type, record = {}) => {
  const normalizedRecord = {
    ...record,
    type,
  }

  if (type === 'exception') {
    normalizedRecord.exception = stringifyException(record.exception)
  }

  return withLogContext(normalizedRecord)
}

export const createLogRequestBodies = (records) => {
  const bodies = []
  let serializedRecords = []
  let batchByteLength = 2

  const finishBatch = () => {
    if (serializedRecords.length === 0) return
    bodies.push(`[${serializedRecords.join(',')}]`)
    serializedRecords = []
    batchByteLength = 2
  }

  records.forEach((record) => {
    let serializedRecord

    try {
      serializedRecord = JSON.stringify(record)
    } catch {
      return
    }

    if (!serializedRecord) return

    const recordByteLength = getUtf8ByteLength(serializedRecord)
    if (recordByteLength + 2 > LOG_REQUEST_MAX_BYTES) return

    const separatorByteLength = serializedRecords.length > 0 ? 1 : 0
    const exceedsBatchLimit =
      serializedRecords.length >= LOG_REQUEST_MAX_RECORDS ||
      batchByteLength + separatorByteLength + recordByteLength >
        LOG_REQUEST_MAX_BYTES

    if (exceedsBatchLimit) {
      finishBatch()
    }

    serializedRecords.push(serializedRecord)
    batchByteLength += (serializedRecords.length > 1 ? 1 : 0) + recordByteLength
  })

  finishBatch()
  return bodies
}

const clearFlushTimer = () => {
  if (!flushTimer) return
  clearTimeout(flushTimer)
  flushTimer = null
}

const postLogBody = (body) => {
  if (typeof fetch !== 'function') return Promise.resolve()

  try {
    return Promise.resolve(
      fetch(LOG_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'same-origin',
        keepalive: true,
      }),
    ).catch(() => undefined)
  } catch {
    return Promise.resolve()
  }
}

export const flushLogRecords = () => {
  clearFlushTimer()

  const records = pendingRecords.splice(0)
  const requestBodies = createLogRequestBodies(records)

  return Promise.allSettled(requestBodies.map(postLogBody))
}

const scheduleFlush = () => {
  if (flushTimer || pendingRecords.length === 0) return

  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushLogRecords()
  }, LOG_FLUSH_INTERVAL_MS)
}

export const enqueueLogRecord = (record) => {
  if (!isObject(record)) return

  if (pendingRecords.length >= LOG_QUEUE_LIMIT) {
    pendingRecords.shift()
  }

  pendingRecords.push(record)

  if (pendingRecords.length >= LOG_FLUSH_RECORD_THRESHOLD) {
    void flushLogRecords()
    return
  }

  scheduleFlush()
}

export const setAuthenticatedLogContext = (userHash, organizationId) => {
  authenticatedContext = {
    userHash: normalizeContextValue(userHash),
    organizationId: normalizeContextValue(organizationId),
  }
}

export const clearAuthenticatedLogContext = () => {
  authenticatedContext = {
    userHash: null,
    organizationId: null,
  }
}

export const telemetryClient = {
  trackTrace: (record) => {
    try {
      enqueueLogRecord(normalizeRecord('trace', record))
    } catch {
      // Logging must never affect application behavior.
    }
  },
  trackException: (record) => {
    try {
      enqueueLogRecord(normalizeRecord('exception', record))
    } catch {
      // Logging must never affect application behavior.
    }
  },
  trackMetric: (record) => {
    try {
      enqueueLogRecord(normalizeRecord('metric', record))
    } catch {
      // Logging must never affect application behavior.
    }
  },
}

const registerWebVitals = () => {
  if (webVitalsRegistered) return
  webVitalsRegistered = true

  const trackWebVital = ({ name, value, rating }) => {
    telemetryClient.trackMetric({
      name: `WebVital_${name}`,
      average: value,
      properties: {
        rating,
        appContext: getAppContext(),
      },
    })
  }

  onCLS(trackWebVital)
  onFCP(trackWebVital)
  onINP(trackWebVital)
  onLCP(trackWebVital)
  onTTFB(trackWebVital)
}

export const initLogTransport = () => {
  if (initialized) return
  initialized = true

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      void flushLogRecords()
    })
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void flushLogRecords()
      }
    })
  }

  registerWebVitals()
}
